import { Box, Typography, Tooltip } from '@mui/material';
import { useTheme, SxProps, Theme } from '@mui/material/styles';
import { InfoIcon } from '../icons';
import LongArrowDown from './icons/LongArrowDown';
import LongArrowUp from './icons/LongArrowUp';
import LoaderComponent from 'components/Loader';
import { ITSM_TILES_CONFIG, SUB_TILES_BY_PARENT } from '../constants';

interface TileSummaryItem {
  total: number,
  lastHour: number,
  previousHour: number,
  difference: number,
  trend: string
}

interface ITSMTileSummaryItem {
  current: number;
  previous: number;
  difference: number;
  trend: string;
}

export interface SeverityTilesProps {
  summary: {
    criticalAlerts?: TileSummaryItem;
    majorAlerts?: TileSummaryItem;
    minorAlerts?: TileSummaryItem;
    warningAlerts?: TileSummaryItem;
    information?: TileSummaryItem;
    closedAlerts?: TileSummaryItem;
    incidents?: ITSMTileSummaryItem;
    problemTickets?: ITSMTileSummaryItem;
  } | null;
  activeTile?: string;
  parentTile?: string;
  onTileClick: (key: string) => void;
  isLoading: boolean;
  timePeriod: string;
  selectedBgColor?: string;
  defaultBgColor?: string;
  selectedTextColor?: string;
  defaultTextColor?: string;
  type?: string;
  sx?: SxProps<Theme>;
  duplicateCounts?: Record<string, number>;
  alertCountView?: 'overall' | 'unique';
}

export interface TileConfig {
  key: string;
  label: string;
  summaryKey: string;
  disabled?: boolean;
}

export const TILES_CONFIG: TileConfig[] = [
  { key: 'Critical', label: 'Critical Alerts', summaryKey: 'criticalAlerts', disabled: false },
  { key: 'Major', label: 'Major Alerts', summaryKey: 'majorAlerts', disabled: false },
  { key: 'Minor', label: 'Minor Alerts', summaryKey: 'minorAlerts', disabled: false },
  { key: 'Warning', label: 'Warning Alerts', summaryKey: 'warningAlerts', disabled: false },
  { key: 'Closed', label: 'Closed Alerts ', summaryKey: 'closedAlerts', disabled: false },
  { key: 'EWSCritical', label: 'EWS Critical Alerts', summaryKey: 'ewsCriticalAlerts', disabled: true },
  { key: 'EWSMajor', label: 'EWS Major Alerts', summaryKey: 'ewsMajorAlerts', disabled: true },
  { key: 'EWSMinor', label: 'EWS Minor Alerts', summaryKey: 'ewsMinorAlerts', disabled: true },
];

const SeverityTiles = ({
  summary,
  activeTile,
  parentTile,
  onTileClick,
  isLoading,
  timePeriod,
  selectedBgColor,
  defaultBgColor,
  selectedTextColor,
  defaultTextColor,
  type,
  sx,
  duplicateCounts = {},
  alertCountView = 'overall',
}: SeverityTilesProps) => {
  const theme = useTheme();
  const p = theme.palette;

  const activeBackground = selectedBgColor ?? p.white.main;
  const inactiveBackground = defaultBgColor ?? p.primary.main;
  const activeText = selectedTextColor ?? p.primary.main;
  const inactiveText = defaultTextColor ?? p.white.main;

  let tiles: TileConfig[] = [];
  if (type === 'ITSM') {
    tiles = ITSM_TILES_CONFIG;
  } else if (type) {
    tiles = SUB_TILES_BY_PARENT[type];
  } else {
    tiles = TILES_CONFIG;
  }
  return (
    <Box sx={{ display: 'flex', gap: 0.5, width: '90%', ...sx }}>
      {tiles.map((tile, idx) => {
        const data = summary ? summary[tile.summaryKey as keyof typeof summary] : undefined;
        const isActive = activeTile === tile.key;
        const isParentActive = parentTile === tile.label
        // Handle both EWS alerts (with .total and .trend) and ITSM (with .current and .trend)
        const isItsm = type === 'ITSM';
        let displayValue: number = 0;

        if (data) {
          if (isItsm === undefined || type === undefined) {
            displayValue = (data as TileSummaryItem).total;
          }
          else if (isItsm || type === "Incident" || type === "Incidents" || type === "Problem Tickets" || type === "Service Requests" || type === "Change Requests") {
            displayValue = (data as ITSMTileSummaryItem).current;
          } else {
            displayValue = (data as TileSummaryItem).total;
          }
        }

        const duplicateCount = duplicateCounts[tile.summaryKey] ?? 0;

        const finalDisplayValue = displayValue;

        const displayDifference = data?.difference ?? 0;
        const trendUp = data?.trend === 'INCREASED';
        const trendDown = data?.trend === 'DECREASED';

        return (
          <Box
            key={tile.key}
            onClick={tile.disabled ? () => { } : () => onTileClick(tile.key)}
            sx={{
              flex: 1,
              p: 2,
              gap: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              cursor: 'pointer',
              position: 'relative',
              backgroundColor: (() => {
                if (tile.label === 'EWS Critical Alerts') {
                  return displayValue === 0 ? '#71758F' : '#f44336';
                }

                if (tile.label === 'EWS Major Alerts') {
                  return displayValue === 0 ? '#71758F' : '#ff9800';
                }

                if (tile.label === 'EWS Minor Alerts') {
                  return displayValue === 0 ? '#71758F' : '#ffeb3b';
                }

                return (isActive || isParentActive)
                  ? activeBackground
                  : inactiveBackground;
              })(),
              border: 'none',
              borderRadius: '4px',
              transition: 'background-color 0.2s ease',
              '&:hover': {
                backgroundColor: activeBackground,
                '& *': { color: `${activeText} !important` },
                '& svg path': { fill: `${activeText} !important` }
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%' }}>
              <Typography
                variant="body1"
                sx={{
                  color: (isActive || isParentActive) ? activeText : inactiveText,
                  fontWeight: 500,
                  lineHeight: 1.2,
                }}
              >
                {tile.label}
              </Typography>
              <Tooltip title={`${tile.label} over the ${timePeriod}`} placement="top">
                <Box sx={{ cursor: 'help', mt: '1px', flexShrink: 0, display: 'flex' }}>
                  <InfoIcon size={20} color={p.info.main} />
                </Box>
              </Tooltip>
            </Box>

            <Typography
              variant="h3"
              sx={{ fontWeight: 'medium', color: (isActive || isParentActive) ? activeText : inactiveText, lineHeight: 1.1 }}
            >
              {isLoading ? <LoaderComponent /> : (finalDisplayValue ?? 0).toLocaleString('en-US')}
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                columnGap: 1,
                rowGap: 0.5,
                width: '100%',
                minWidth: 0,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
                {trendUp && <LongArrowUp color={(isActive || isParentActive) ? activeText : inactiveText} />}
                {trendDown && <LongArrowDown color={(isActive || isParentActive) ? activeText : inactiveText} />}
                <Typography variant="bodyText" sx={{ color: (isActive || isParentActive) ? activeText : inactiveText, lineHeight: 1 }}>
                  {isLoading ? '...' : Math.abs(displayDifference ?? 0)}
                </Typography>
                <Typography variant="bodyText" sx={{ color: (isActive || isParentActive) ? activeText : inactiveText, lineHeight: 1 }}>
                  From {`${timePeriod}`}
                </Typography>
              </Box>

              {!isLoading && alertCountView === 'unique' && duplicateCount > 0 && (
                <Typography
                  sx={{
                    color: p.widget.text.duplicateCount,
                    fontSize: '12px',
                    fontWeight: 500,
                    marginLeft: 'auto',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {duplicateCount.toLocaleString('en-US')} Duplicate
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default SeverityTiles;
